import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useApolloClient, useMutation } from '@apollo/client';
import { promiseModalDialog, toastFailure, toastSuccess } from 'motion-components';
import { useTranslation } from 'react-i18next';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { FaPlus } from 'react-icons/fa';
import { promiseModalSelectMultiCollectionpoints } from 'shared-pages/components';
import { gqlAddNotificationRecipient, gqlGetCollectionpointsForNotification } from '../graphql';
import { NotificationRecipientStatus } from '../constants';

type AddStats = { added: number; noEmail: number; skipped: number };

/**
 * Προσθήκη παραληπτών από modal επιλογής σημείων συλλογής.
 * Δύο τρόποι: τα τσεκαρισμένα σημεία ή όλα όσα ταιριάζουν στο φίλτρο του modal.
 */
export const AddCollectionpointsButton = ({
  notificationId,
  onAdded,
}: {
  notificationId: number;
  onAdded: () => void;
}) => {
  const { t } = useTranslation();
  const { appStore, tableStore } = useRootStore();
  const { handleGraphqlError } = useHandleError('AddCollectionpointsButton');
  const client = useApolloClient();
  const [addRecipient] = useMutation(gqlAddNotificationRecipient);
  const [busy, setBusy] = useState(false);

  /** Το email είναι στιγμιότυπο· χωρίς email ο παραλήπτης μένει σε κατάσταση «Χωρίς email» */
  async function addRows(rows: ReadonlyArray<any>, stats: AddStats): Promise<AddStats> {
    for (const cp of rows) {
      const email = (cp.email ?? '').trim() || null;
      try {
        await addRecipient({
          variables: {
            data: {
              notificationId,
              collectionpointId: cp.id,
              email,
              statusId: email
                ? NotificationRecipientStatus.Queued
                : NotificationRecipientStatus.NoEmail,
              isActive: true,
            },
          },
        });
        stats.added += 1;
        if (!email) stats.noEmail += 1;
      } catch {
        // Διπλοεγγραφή (notificationId + collectionpointId) — το προσπερνάμε
        stats.skipped += 1;
      }
    }
    return stats;
  }

  /** Σελιδοποιημένη ανάγνωση για να μη φορτώνονται χιλιάδες σημεία μαζί */
  async function addByFilter(where: string, stats: AddStats): Promise<AddStats> {
    const limit = 200;
    let offset = 0;
    for (;;) {
      const res = await client.query({
        query: gqlGetCollectionpointsForNotification,
        variables: { where, offset, limit, order: 'id ASC' },
        fetchPolicy: 'network-only',
      });
      const rows = res.data?.getCollectionpoints?.data ?? [];
      if (rows.length === 0) break;
      await addRows(rows, stats);
      if (rows.length < limit) break;
      offset += limit;
    }
    return stats;
  }

  async function handleClick() {
    const res = await promiseModalSelectMultiCollectionpoints({
      appStore,
      tableStore,
      title: t('Επιλογή Σημείων Συλλογής'),
      initFilters: { isActive: true },
    });
    if (!res) return;

    // Το «όσα ταιριάζουν στο φίλτρο» μπορεί να είναι μαζική προσθήκη — ζητάμε επιβεβαίωση
    if (res.mode === 'filter') {
      const confirmed = await promiseModalDialog({
        title: t('Προσθήκη σημείων'),
        text: t(
          'Θα προστεθούν στους παραλήπτες όλα τα σημεία που ταιριάζουν στο φίλτρο ({{count}}). Συνέχεια;',
          { count: res.count },
        ),
      });
      if (!confirmed) return;
    }

    setBusy(true);
    try {
      const stats: AddStats = { added: 0, noEmail: 0, skipped: 0 };
      if (res.mode === 'filter') await addByFilter(res.where, stats);
      else await addRows(res.rows, stats);

      toastSuccess(
        t('Προστέθηκαν {{added}} σημεία (χωρίς email: {{noEmail}}, ήδη υπήρχαν: {{skipped}})', {
          added: stats.added,
          noEmail: stats.noEmail,
          skipped: stats.skipped,
        }),
      );
      onAdded();
    } catch (err: any) {
      handleGraphqlError('add-recipients', err);
      toastFailure(err?.message ?? t('Αποτυχία προσθήκης σημείων'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button size="sm" onClick={handleClick} disabled={busy}>
      <FaPlus className="me-1" />
      {busy ? t('Προσθήκη...') : t('Προσθήκη σημείων συλλογής')}
    </Button>
  );
};
